import { common, enums, fields, resources } from '../gads'
import { GadsFieldsRequired } from '../utils/global'
import { CampaignGroup } from '../targets'

export namespace Segments {
    export interface Filters {
        selected_group: Partial<CampaignGroup>
    }

    export type AllSegments = {
        [segment in Segment]: { list: Segments.FormattedSegment[]; existsForPlatform: boolean }
    }

    export interface GetAllSegmentsResponse {
        segments: AllSegments
        worst_segments: FormattedSegment[]
        best_segments: FormattedSegment[]
    }

    export type AllReports =
        | CampaignReport
        | KeywordReport
        | GeoReport
        | SearchTermReport
        | AdGroupAdReport
        | AgeRangeReport
        | GenderReport
        | LandingPageReport

    export type CampaignReport = {
        campaign: GadsFieldsRequired<resources.Campaign, 'advertising_channel_type' | 'name'>
        segments: GadsFieldsRequired<
            common.Segments,
            'device' | 'hour' | 'day_of_week' | 'geo_target_city' | 'geo_target_country'
        >
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type KeywordReport = {
        ad_group_criterion: GadsFieldsRequired<resources.AdGroupCriterion, 'keyword'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type GeoReport = {
        geographic_view: GadsFieldsRequired<resources.GeographicView, 'country_criterion_id'>
        segments: GadsFieldsRequired<common.Segments, 'geo_target_city' | 'geo_target_country'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type SearchTermReport = {
        search_term_view: GadsFieldsRequired<resources.SearchTermView, 'search_term'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
        word?: string
        word_pair?: string
        word_trio?: string
    }

    export type AdGroupAdReport = {
        ad_group: GadsFieldsRequired<resources.AdGroupAd, 'ad'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type AgeRangeReport = {
        age_range_view: GadsFieldsRequired<resources.AgeRangeView, 'resource_name'>
        ad_group_criterion: GadsFieldsRequired<resources.AdGroupCriterion, 'criterion_id'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type GenderReport = {
        gender_view: GadsFieldsRequired<resources.GenderView, 'resource_name'>
        ad_group_criterion: GadsFieldsRequired<resources.AdGroupCriterion, 'criterion_id'>
        metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'all_conversions'>
    }

    export type LandingPageReport = {
        landing_page_view: GadsFieldsRequired<
            resources.LandingPageView,
            'resource_name' | 'unexpanded_final_url'
        >
        metrics: GadsFieldsRequired<
            common.Metrics,
            'cost_micros' | 'all_conversions' | 'all_conversions_value'
        >
        campaign: GadsFieldsRequired<
            resources.Campaign,
            'resource_name' | 'status' | 'serving_status' | 'id'
        >
    }

    export enum Segment {
        CAMPAIGN = 'campaign',
        CPC_BANDS = 'cpc_bands',
        DEVICE = 'device',
        ADVERTISING_CHANNEL = 'advertising_channel',
        AGE_RANGE = 'age_range',
        GENDER = 'gender',
        COMPANY = 'company',
        COMPANY_SIZE = 'company_size',
        MEMBER_SENIORITY = 'member_seniority',
        MEMBER_INDUSTRY = 'member_industry',
        MEMBER_JOB_TITLE = 'member_job_title',
        MEMBER_JOB_FUNCTION = 'member_job_function',
        HOUR_OF_DAY = 'hour_of_day',
        DAY_OF_WEEK = 'day_of_week',
        MATCH_TYPE = 'match_type',
        COUNTRY = 'country',
        STATE = 'state',
        CITY = 'city',
        WORD = 'word',
        WORD_2 = 'word2',
        WORD_3 = 'word3',
        LANDING_PAGE_URL = 'landing_page_url',
        LANDING_PAGE_VIEW = 'landing_page_view',
        NETWORK = 'network',
        INCOME_RANGE = 'income_range',
        PARENTAL_STATUS = 'parental_status',
        PRODUCT = 'product',
    }

    export interface FormattedSegment {
        target_segment: Segment
        segment_label: string
        real_name: string
        conversions: number
        conversions_value: number
        cost: number
        cpa: number
        roas: number
        difference: number
        clicks?: number
        cost_percent?: number
        conv_percent?: number
        conv_value_percent?: number
        avg_cpa?: number
        avg_roas?: number
    }

    export type GadsEntity = Partial<fields.Resource>

    export type SegmentsInfo = {
        [segment in Segment]: {
            entity: GadsEntity
            groupByKey: fields.Attribute | fields.Segment | string
            formatSegmentLabel?: ({
                all_segments,
                segment_key,
            }: {
                all_segments?: AllReports[]
                segment_key: enums.Device | string | number
            }) => string
            formatWarehouseSegmentLabel?: (segment_key: string) => string
            attributeRealName: string
        }
    }
}
